import React, { useContext, useMemo } from 'react';
import { Empty } from '@maxtropy/components';
import { ShareDataUnityEnergyCockpitMapContext } from '../../../..';

// 颜色映射
const colorMap: Record<string, string> = {
  red: '#FF4D4F',
  orange: '#FA8C16',
  yellow: '#FAAD14',
  green: '#52C41A',
  blue: '#1890FF',
  purple: '#722ED1',
  black: '#000000',
  white: '#FFFFFF',
  gray: '#8C8C8C',
  brown: '#A52A2A',
  silver: '#C0C0C0',
  gold: '#FFD700',
};

// 解析带颜色标记的文本，支持多个颜色标记
// 格式：#ColorName文本内容#ColorName
// 例如：#Red红色文字#Red #Green绿色文字#Green
const parseColoredText = (text: string): React.ReactNode[] => {
  if (!text) return [];

  // 正则匹配：#ColorName(内容)#ColorName
  // 使用非贪婪匹配，并且结尾标记必须与开头标记相同
  const colorTagPattern = /#(\w+)(.*?)#\1/g;

  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let keyIndex = 0;

  // 重置正则的 lastIndex
  colorTagPattern.lastIndex = 0;

  let match;
  while ((match = colorTagPattern.exec(text)) !== null) {
    const [fullMatch, colorName, content] = match;
    const matchStart = match.index;
    const matchEnd = matchStart + fullMatch.length;

    // 处理匹配之前的普通文本
    if (matchStart > lastIndex) {
      const plainText = text.substring(lastIndex, matchStart);
      result.push(<React.Fragment key={keyIndex++}>{renderContent(plainText)}</React.Fragment>);
    }

    // 处理带颜色的文本
    const color = colorMap[colorName.toLowerCase()];
    if (color) {
      result.push(
        <span key={keyIndex++} style={{ color }}>
          {renderContent(content)}
        </span>
      );
    } else {
      // 如果颜色名称不匹配，作为普通文本处理
      result.push(<React.Fragment key={keyIndex++}>{renderContent(fullMatch)}</React.Fragment>);
    }

    lastIndex = matchEnd;
  }

  // 处理最后剩余的普通文本
  if (lastIndex < text.length) {
    const plainText = text.substring(lastIndex);
    result.push(<React.Fragment key={keyIndex++}>{renderContent(plainText)}</React.Fragment>);
  }

  return result;
};

// 处理换行符，将 \n 转换为 <br />
const renderContent = (text: string): React.ReactNode[] => {
  // 合并连续的换行符为单个换行
  const lines = text.replace(/\n+/g, '\n').split('\n');
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

const EnergySaving = () => {
  const { configDetail } = useContext(ShareDataUnityEnergyCockpitMapContext);
  const content = configDetail?.energySaving;

  const coloredContent = useMemo(() => {
    if (!content) return null;
    return parseColoredText(content);
  }, [content]);

  if (!content) {
    return (
      <div style={{ padding: 16 }}>
        <Empty />
      </div>
    );
  }

  return (
    <div className="energy-saving-container" style={{ padding: 16 }}>
      <div
        className="energy-saving-content-box"
        style={{
          background: 'rgba(74, 144, 226, 0.2)',
          borderRadius: 2,
          padding: 16,
        }}
      >
        <div
          className="energy-saving-text-content"
          style={{
            fontSize: 14,
            color: 'rgba(255, 255, 255, 0.85)',
            lineHeight: 2,
          }}
        >
          {coloredContent}
        </div>
      </div>
    </div>
  );
};

export default EnergySaving;
